import { IHlaData } from './hla-data';
import { IDonorPreselectNote } from './donor-preselect-note';
import { ILkdpiInfo } from './recipients/pre-select-result';
import { ISingleEpletMmRisk } from './single-eplet-mm-risk';
import { PotentialDonorCategory } from '@nkr/enums/potential-donor-category';
import { DonorPreselectReviewStatus } from '@nkr/enums/donor-preselect-review-status';

/**
 * The donor fields the recipient pre-select grid renders.
 *
 * Deliberately NOT IPotentialDonor. That interface mirrors the full donor contract - roughly 195
 * properties reached the browser per row - where this page reads 38. Field names are identical to
 * IPotentialDonor's, so nothing in the grid had to change how it reads a value; only the type.
 *
 * IPotentialDonor is untouched and still used by the other 18 views that consume it.
 */
export interface IPreselectDonorInfo {
    id: number,
    centerId: number,
    alias: string,

    age: number,
    weight: number,
    height: number,
    bmi: number,
    gender: string,
    race: string,
    fullBloodType: string,
    cmv: string,
    ebv: string,
    creatinineClearance: number,
    serumCreatinine: number,
    hasHistoryOfSmoking: boolean,
    sbp: number,
    medicalComments: string,
    donorCtAbnormalities: string,
    numberOfArteries: number,
    whichKidney: string,
    whichKidneyAbbreviated: string,
    donorTypeId: number,
    statusId: number,
    isDeleted: boolean,

    /** Flattened from the nested center objects: the grid only ever read alias and state. */
    centerAlias: string,
    /** Already resolves the RDN-center-over-donor-center fallback the grid used to do itself. */
    centerState: string,
    rdnCenterId: number,
    rdnCenterAlias: string,

    isAccepted: boolean,
    isFullyConfident: boolean,
    isReEvalNeeded: boolean,
    reviewStatus: DonorPreselectReviewStatus,
    isReviewRequested: boolean,
    reviewRequestedDate: string,
    responseDeadlineDate: string,

    pairMatchPower: number,
    hasAcceptableXm: boolean,
    hasPendingXm: boolean,

    isAboCompatible: boolean,
    isBiologicalMatch: boolean,
    isBioMatch: boolean,
    isAvoided: boolean,
    isBadAboMatch: boolean,
    isBadAgeMatch: boolean,
    isBadPmpc: boolean,
    isInMatch: boolean,
    isPediatricAgeCutoff: boolean,
    isPotentialDonor: boolean,
    isHardToMatch: boolean,

    hlaData: IHlaData
}

/**
 * One row of the recipient pre-select grid.
 *
 * The compatibility members the grid uses are flat here rather than nested under a
 * compatibilityResult, and the per-row recipient copy is gone - the page-level recipient serves
 * the one consumer that needed it.
 */
export interface IPreselectDonorDetail {
    donor: IPreselectDonorInfo,

    failedAvoids: string[],
    hlaPointsA: number,
    hlaPointsB: number,
    hlaPointsDr: number,
    totalHlaPoints: number,
    isHardBlocked: boolean,

    singleEpletMmRisk: ISingleEpletMmRisk,
    lkdpiInfo: ILkdpiInfo,
    categories: PotentialDonorCategory[],
    isPairedDonor: boolean,
    isRequired: boolean,
    reviewStatus: DonorPreselectReviewStatus,
    donorNote: IDonorPreselectNote,
    matchNote: IDonorPreselectNote,
    bmtMm: number,
    matchOfferId: number,
    isReviewRequested: boolean,
    reviewRequestedDate: string,
    responseDeadLineDate: string
}
